import { useMerchantCabinetConfig } from '../../config/ConfigProvider';
import { QueryClient, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { groupByPredicate } from '@payler/utils';
import dayjs from 'dayjs';
import { PaginationProps } from '@payler/ui-components';
import { useFasterPayoutsFiltersFromUrl } from './url';
import { TFasterPayoutSourceFilter, ApiMerchantCabinet, TFasterPayout, TFasterPayoutsQueryParams, TFasterPayoutStatus } from '../../../../../api/src';
import { usePagerPropsFromUrl } from '../pager/use-pager-props-from-url';
import { usePagerUrlCallbacks } from '../pager/use-pager-url-callbacks';
import { TFasterPayoutComputedHistoryItem } from './types';
import { DEFAULT_PAGER_PROPS } from '../../const';
import { useUIDateFormat } from '../use-ui-dateFormat';
import { useURLContractId } from '../../layouts/ContractIdLayout/ContractIdLayout';
import { useApi } from '../../context/ApiContextProvider';

const FASTER_PAYOUTS_STALE_TIME = 60 * 1000;
export const FASTER_PAYOUT_STATUS_POLL_INTERVAL_MS = 2000;

export const FASTER_PAYOUT_PROCESSING_STATUSES: TFasterPayoutStatus[] = [
  'Created',
  'Ready',
  'Pending',
];

export const isFasterPayoutProcessingStatus = (
  status: TFasterPayoutStatus,
): boolean => FASTER_PAYOUT_PROCESSING_STATUSES.includes(status);

export const isMcFasterPayoutsListQuery = ({
  queryKey,
}: {
  queryKey: readonly unknown[];
}) => {
  const filters = queryKey[4];
  return (
    queryKey[0] === 'faster-payouts' &&
    queryKey.length === 5 &&
    typeof filters === 'object' &&
    filters !== null &&
    'Source' in filters &&
    (filters as { Source?: TFasterPayoutSourceFilter }).Source === 'mc'
  );
};

export const invalidateMcFasterPayoutsQueries = (
  queryClient: QueryClient,
  contractId: number,
) =>
  queryClient.invalidateQueries({
    predicate: (query) =>
      isMcFasterPayoutsListQuery(query) && query.queryKey[2] === contractId,
  });

export const getMcFasterPayoutIdsFromCache = (
  queryClient: QueryClient,
  contractId: number,
): Set<number> => {
  const ids = new Set<number>();
  queryClient
    .getQueriesData({
      predicate: (query) =>
        isMcFasterPayoutsListQuery(query) && query.queryKey[2] === contractId,
    })
    .forEach(([, data]) => {
      if (data && typeof data === 'object' && 'items' in data) {
        const items = (data as { items: { id: number }[] }).items;
        items.forEach((item) => ids.add(item.id));
      }
    });
  return ids;
};

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export type TFasterPayoutStatusResult = Pick<TFasterPayout, 'id' | 'status'>;

export const waitForFasterPayoutFinalStatus = async (
  api: ApiMerchantCabinet,
  contractId: number,
  payoutId: number,
  maxAttempts = 30,
  delayMs = 2000,
): Promise<TFasterPayoutStatusResult | null> => {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const data = await api.getFasterPayoutWithHistory(contractId, payoutId);
    if (data.status === 'Success' || data.status === 'Rejected') {
      return { id: data.id, status: data.status };
    }
    if (attempt < maxAttempts - 1) {
      await sleep(delayMs);
    }
  }
  return null;
};

export const findNewMcFasterPayout = async (
  api: ApiMerchantCabinet,
  contractId: number,
  dateFormat: string,
  knownIds: Set<number>,
  maxAttempts = 5,
  delayMs = 400,
): Promise<TFasterPayout | null> => {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const data = await api.getFasterPayouts(
      contractId,
      { Source: 'mc', Page: 1, PageSize: 20 },
      dateFormat,
    );
    const newPayouts = data.items
      .filter((item) => !knownIds.has(item.id))
      .sort((a, b) => (a.created < b.created ? 1 : -1));
    if (newPayouts[0]) {
      return newPayouts[0];
    }
    if (attempt < maxAttempts - 1) {
      await sleep(delayMs);
    }
  }
  return null;
};

const FASTER_PAYOUT_DATA_STALE_TIME = 60 * 1000;
const FASTER_PAYOUTS_TEMPLATES_STALE_TIME = 600 * 1000;

type TFasterPayoutsQueryOptions = {
  source?: TFasterPayoutSourceFilter;
  useUrlFilters?: boolean;
  pollWhileLatestProcessing?: boolean;
};

export const useFasterPayoutsQuery = (options?: TFasterPayoutsQueryOptions) => {
  const api = useApi();
  const { culture } = useMerchantCabinetConfig();
  const contractId = useURLContractId();
  const pagerData = usePagerPropsFromUrl();
  const urlFilters = useFasterPayoutsFiltersFromUrl();
  const dateFormat = useUIDateFormat();
  const useUrlFilters = options?.useUrlFilters ?? true;
  const pollWhileLatestProcessing = options?.pollWhileLatestProcessing ?? false;
  const source = options?.source ?? urlFilters.Source ?? undefined;
  const urlFilterValues = useUrlFilters
    ? (({ Source: _source, ...rest }) => rest)(urlFilters)
    : {};
  const filters: TFasterPayoutsQueryParams = {
    ...urlFilterValues,
    ...(source ? { Source: source } : {}),
  };
  if (!contractId) throw new Error('no contractId');
  return useQuery({
    queryKey: ['faster-payouts', culture, contractId, pagerData, filters],
    queryFn: async () => {
      const data = await api.getFasterPayouts(
        contractId,
        {
          ...filters,
          ...pagerData,
        },
        dateFormat,
      );

      const grouped = groupByPredicate(data.items, (v) => {
        const dateString = v['created'] as string;
        return dayjs(dateString).format('YYYY-MM-DD');
      });
      return { ...data, grouped };
    },
    staleTime: FASTER_PAYOUTS_STALE_TIME,
    refetchInterval: (query) => {
      if (!pollWhileLatestProcessing) {
        return false;
      }
      const page = (query.queryKey[3] as { Page?: number } | undefined)?.Page ?? 1;
      if (page !== 1) {
        return false;
      }
      const latest = query.state.data?.items?.[0];
      if (latest && isFasterPayoutProcessingStatus(latest.status)) {
        return FASTER_PAYOUT_STATUS_POLL_INTERVAL_MS;
      }
      return false;
    },
  });
};

export const useMcFasterPayoutsQuery = () =>
  useFasterPayoutsQuery({
    source: 'mc',
    useUrlFilters: false,
    pollWhileLatestProcessing: true,
  });

export const useFasterPayoutsQueryPagerProps = (
  options?: TFasterPayoutsQueryOptions,
) => {
  const payments = useFasterPayoutsQuery(options);
  const { goToPage, setPageSize } = usePagerUrlCallbacks();
  if (payments.status !== 'success') {
    return { ...DEFAULT_PAGER_PROPS, goToPage, setPageSize } as PaginationProps;
  }
  const { page, pageSize, totalItems } = payments.data.paginationMetadata;
  return {
    goToPage,
    setPageSize,
    pageSize,
    currentPage: page,
    totalRecords: totalItems,
  } as PaginationProps;
};

export const useFasterPayoutDataQuery = (id: number) => {
  const api = useApi();
  const contractId = useURLContractId();
  if (!contractId) throw new Error('no contract id');
  const { culture } = useMerchantCabinetConfig();
  return useQuery({
    queryKey: ['faster-payouts', culture, contractId, id],
    queryFn: async () => {
      const data = await api.getFasterPayoutWithHistory(contractId, id);
      const history: TFasterPayoutComputedHistoryItem[] = [];
      data.operations.forEach((op) => {
        op.history.forEach((h) => {
          history.push({
            type: op.type,
            typeDescription: op.typeDescription,
            ...h,
          });
        });
      });
      return {
        ...data,
        history: history.sort((a, b) => (a.createdAt < b.createdAt ? -1 : 1)),
      };
    },
    staleTime: FASTER_PAYOUT_DATA_STALE_TIME,
  });
};

export const useFasterPayoutsTemplatesQuery = () => {
  const api = useApi();
  const contractId = useURLContractId();
  if (!contractId) throw new Error('no contract Id');
  return useSuspenseQuery({
    queryKey: ['faster-payouts', 'templates', contractId],
    queryFn: async () => api.getFasterPayoutsTemplates(contractId),

    staleTime: FASTER_PAYOUTS_TEMPLATES_STALE_TIME,
  });
};
